import { useFeature } from "@/hooks/useFeature";
import { usePermissions } from "@/hooks/usePermissions";
import { assessmentNext, decisionLabels } from "@/lib/companyAssessment";
import { growthActionLabel, growthCompanyPath } from "@/lib/usGrowth";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import {
  getGrowthQueue,
  type GrowthFilter,
  type GrowthQueue,
  type GrowthView,
} from "@/api/usGrowth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UsGrowthDossier, growthError, PriorityBadge } from "./UsGrowthDossier";

const views: [GrowthView, string][] = [
  ["data_pending", "Données CFO à compléter"],
  ["scored", "Scoring complet"],
  ["qualified", "Sociétés qualifiées CFO"],
  ["not_qualified", "Hors critères CFO"],
  ["pending", "Founders à vérifier"],
  ["partial", "Founders partiellement vérifiés"],
  ["complete", "Tous les founders vérifiés"],
  ["waiting", "Preuve externe à rechercher"],
  ["blocked", "Bloquées / exclues"],
];
const filters: [GrowthFilter, string][] = [
  ["data_missing", "Données CFO manquantes"],
  ["data_stale", "Données CFO périmées"],
  ["data_conflict", "Preuves contradictoires"],
  ["founder_incomplete", "Vérification founder incomplète"],
  ["email_missing", "Email manquant"],
  ["email_risky", "Email ACCEPT_ALL / RISKY"],
  ["identity", "Identité à confirmer"],
  ["affiliation", "Affiliation à confirmer"],
  ["attribution", "Attribution insuffisante"],
  ["eligibility", "Éligibilité à réexaminer"],
  ["suppression", "Suppressions en attente"],
  ["stale", "Vérification ancienne / absente"],
  ["high_priority", "Priorité élevée"],
];

export function UsGrowthQueue({ slug }: { slug: string }) {
  const { canViewScore: scorePermission } = usePermissions();
  const scoringEnabled = useFeature("scoring");
  const canViewScore = scorePermission && scoringEnabled;
  const [view, setView] = useState<GrowthView>(
    canViewScore ? "data_pending" : "pending",
  );
  const [filter, setFilter] = useState<GrowthFilter | "">("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("priority");
  const [page, setPage] = useState(1);
  const [version, setVersion] = useState(0);
  const [data, setData] = useState<GrowthQueue | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    getGrowthQueue(
      slug,
      { view, filter, search: query, sort, page },
      controller.signal,
    )
      .then(setData)
      .catch((e: unknown) => {
        if (!controller.signal.aborted) {
          setData(null);
          setError(growthError(e));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [slug, view, filter, query, sort, page, version]);
  const summary = data?.summary;
  const refresh = () => setVersion((v) => v + 1);
  return (
    <section className="space-y-5" aria-label="US Growth — Sociétés à vérifier">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            US Growth — Sociétés à vérifier
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Complétez les preuves de société pour obtenir une décision CFO.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={loading}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
          />
          Actualiser
        </Button>
      </div>
      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            ["Sociétés évaluées", summary.assessment_companies ?? "—"],
            [
              "Données incomplètes · hors blocages",
              summary.data_pending_companies ?? "—",
            ],
            ["Données CFO complètes", summary.data_complete_companies ?? "—"],
            ["Founders à vérifier", summary.remaining_founders],
          ].map(([label, value]) => (
            <div
              key={label}
              className="rounded-plane border border-slate-200 bg-white px-4 py-3"
            >
              <p className="text-2xl font-semibold tabular-nums text-slate-900">
                {value}
              </p>
              <p className="mt-1 text-xs text-slate-500">{label}</p>
            </div>
          ))}
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-[minmax(12rem,1fr)_13rem_13rem_11rem]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            aria-label="Rechercher une société US Growth"
            placeholder="Société ou domaine…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select
          value={view}
          onValueChange={(v) => {
            setView(v as GrowthView);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full" aria-label="Vue de vérification">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {views
              .filter(
                ([v]) =>
                  (v !== "blocked" || data?.can_view_blocked) &&
                  (canViewScore ||
                    ![
                      "data_pending",
                      "scored",
                      "qualified",
                      "not_qualified",
                    ].includes(v)),
              )
              .map(([v, label]) => (
                <SelectItem key={v} value={v}>
                  {label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <Select
          value={filter || "any"}
          onValueChange={(v) => {
            setFilter(v === "any" ? "" : (v as GrowthFilter));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full" aria-label="Filtrer les obstacles">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Tous les obstacles</SelectItem>
            {filters
              .filter(([v]) => canViewScore || !v.startsWith("data_"))
              .map(([v, label]) => (
                <SelectItem key={v} value={v}>
                  {label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <Select
          value={sort}
          onValueChange={(v) => {
            setSort(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full" aria-label="Trier la file">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="priority">Priorité</SelectItem>
            <SelectItem value="remaining">Fondateurs restants</SelectItem>
            <SelectItem value="oldest">Contrôle le plus ancien</SelectItem>
            <SelectItem value="name">Société A–Z</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {error}
          <Button
            variant="outline"
            size="sm"
            className="ml-3"
            onClick={refresh}
          >
            Réessayer
          </Button>
        </div>
      )}
      <div
        className="overflow-hidden rounded-plane border border-slate-200 bg-white shadow-plane"
        aria-busy={loading}
      >
        <Table className="table-fixed min-w-[780px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[24%] px-4">Société</TableHead>
              <TableHead className="w-[13%]">Fondateurs</TableHead>
              <TableHead className="w-[22%]">Données CFO</TableHead>
              <TableHead className="w-[11%]">Score</TableHead>
              <TableHead>Décision</TableHead>
              <TableHead className="w-24">
                <span className="sr-only">Ouvrir le dossier</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
                  <span className="sr-only">Chargement</span>
                </TableCell>
              </TableRow>
            ) : (
              data?.items.map((row) => (
                <TableRow key={row.company_id}>
                  <TableCell className="px-4 py-4">
                    <button
                      className="max-w-full break-words text-left font-medium text-slate-900 hover:text-indigo-600"
                      onClick={() => setSelected(row.company_id)}
                    >
                      {row.name}
                    </button>
                    <p
                      className="mt-1 truncate text-xs text-slate-500"
                      title={row.domain ?? undefined}
                    >
                      {row.domain || "Domaine inconnu"}
                    </p>
                  </TableCell>
                  <TableCell className="py-4">
                    <p
                      className={`font-medium ${row.remaining ? "text-slate-900" : "text-emerald-700"}`}
                    >
                      {row.remaining
                        ? `${row.remaining} à vérifier`
                        : "Tous vérifiés"}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {row.verified} vérifié{row.verified > 1 ? "s" : ""} sur{" "}
                      {row.founders}
                    </p>
                  </TableCell>
                  <TableCell className="py-4">
                    <p className="text-sm font-medium text-slate-700">
                      {row.assessment
                        ? `${row.assessment.complete_dimensions} / ${row.assessment.total_dimensions} documentées`
                        : "À évaluer"}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {row.assessment
                        ? assessmentNext(row.assessment)
                        : growthActionLabel(row.next_action)}
                    </p>
                  </TableCell>
                  <TableCell className="py-4">
                    <p className="font-semibold tabular-nums">
                      {row.assessment?.score ?? "—"}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {row.assessment?.score != null
                        ? row.assessment.provisional
                          ? "Provisoire"
                          : "Complet"
                        : ""}
                    </p>
                  </TableCell>
                  <TableCell className="py-4 text-xs text-slate-700">
                    {row.assessment
                      ? decisionLabels[row.assessment.decision]
                      : "Non évaluée"}
                    {row.priority === "BLOCKED" && (
                      <div className="mt-1.5">
                        <PriorityBadge priority={row.priority} />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="pr-4 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelected(row.company_id)}
                      aria-label={`Examiner ${row.name}`}
                    >
                      Examiner
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
            {!loading && !error && !data?.items.length && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-12 text-center text-slate-500"
                >
                  Aucune société pour ces critères.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {data && !loading && (
          <div className="flex items-center justify-between border-t px-4 py-3 text-xs text-slate-500">
            <span>
              {data.total} sociétés · page {page} /{" "}
              {Math.max(1, Math.ceil(data.total / data.page_size))}
            </span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                aria-label="Page précédente"
                disabled={page === 1 || loading}
                onClick={() => setPage((v) => v - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Page suivante"
                disabled={page * data.page_size >= data.total || loading}
                onClick={() => setPage((v) => v + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
      {summary && (
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer">
            Détails de la cohorte historique
          </summary>
          <p className="mt-2">
            {summary.historical_uuids} UUID · {summary.founders} personnes
            canoniques · {summary.historical_verified} vérifiées sur le fond à
            l’import · {summary.historical_unverified_uuids} dossiers UUID sans
            validation historique · {summary.excluded_aliases} alias exclus
            conservés.
          </p>
          <p className="mt-1">
            Un email DELIVERABLE ne valide ni l’affiliation ni les suppressions.
            Les contrôles commerciaux restent indépendants.
          </p>
        </details>
      )}
      <Sheet
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>Dossier US Growth</SheetTitle>
            <SheetDescription>
              Diagnostic de la société, preuves et vérification des founders.
            </SheetDescription>
          </SheetHeader>
          {selected !== null && (
            <Link
              className="mt-3 inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline"
              to={growthCompanyPath(slug, selected)}
            >
              Ouvrir la fiche société
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          )}
          {selected !== null && (
            <UsGrowthDossier
              key={`${slug}:${selected}`}
              slug={slug}
              companyId={selected}
              onChanged={refresh}
            />
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}
